import { ChiTietHoaDonBanHangDichVu, ChiTietHoaDonBanHangDichVuInput } from '@/types/schemas';
import { transformDocumentNumber } from '@/components/custom/erp';
import { isValidUUID } from '@/lib/uuid-validator';
import { toFiniteNumber } from '@/lib/formatUtils';
import { MA_CHUNG_TU } from '@/constants';
import { FormFieldState } from '../hooks';

/**
 * Transform service detail rows for API submission
 * @param detailRows - Array of service detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (detailRows: ChiTietHoaDonBanHangDichVu[], formMode: string): BanHangRecord[] => {
  return detailRows.map((row: BanHangRecord, index: number) => ({
    line: index + 1,
    ...(isValidUUID(row.uuid) && formMode !== 'add' ? { uuid: row.uuid } : {}),
    sl_cl: toFiniteNumber(row.so_luong),
    ma_dv: row.ma_dv_data?.uuid || '',
    tk_dt: row.tk_dt_data?.uuid || row.ma_dv_data?.tk_dt || '',
    dvt: row.ma_dv_data?.dvt_data?.uuid || row.dvt || '',
    so_luong: row.so_luong || 0,
    gia_nt2: row.gia_nt2 || 0,
    tien_nt2: row.tien_nt2 || 0,
    dien_giai: row.dien_giai || null,
    tl_ck: row.tl_ck || 0,
    ck_nt: row.ck_nt || 0,
    tk_ck: row.tk_ck_data?.uuid || '',
    ten_tk_ck: row.tk_ck_data?.name || row.ma_dv_data?.tk_ck_data?.name || '',
    ma_thue: row.ma_thue_data?.uuid || '',
    thue_suat: row.ma_thue_data?.thue_suat || 0,
    tk_thue_co: row.tk_thue_co_data?.uuid || '',
    ten_tk_thue_co: row.tk_thue_co_data?.name || '',
    thue_nt: roundNumber(row.thue_nt) || 0,
    gia2: roundNumber(row.gia_ban) || 0,
    tien2: roundNumber(row.thanh_tien) || 0,
    ck: roundNumber(row.tong_ck) || 0,
    thue: roundNumber(row.tong_thue) || 0,

    // Additional fields
    ma_bp: row.ma_bp_data?.uuid || '',
    ma_vv: row.ma_vv_data?.uuid || '',
    ma_hd: row.ma_hd_data?.uuid || '',
    ma_dtt: row.ma_dtt_data?.uuid || '',
    ma_ku: row.ma_ku_data?.uuid || '',
    ma_phi: row.ma_phi_data?.uuid || '',
    ma_sp: row.ma_sp_data?.uuid || null,
    ma_lsx: row.lenh_sx || '',
    ma_cp0: row.ma_cp0_data?.uuid || ''
  }));
};

/**
 * Transform all service invoice form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of service detail row data
 * @param totalAmount - Total amount calculated
 * @param totalDiscount - Total discount calculated
 * @param totalTax - Total tax calculated
 * @param entity - Current entity information
 * @returns Transformed data ready for API submission
 */
export const transformFormData = (
  data: BanHangPayload,
  state: FormFieldState,
  detailRows: ChiTietHoaDonBanHangDichVu[],
  calculateTotals: BanHangRecord,
  formMode: string
) => {
  // Transform detail and tax rows
  const chi_tiet = transformDetailRows(detailRows, formMode);

  // Build the final form data object
  return {
    // Basic entity and document info
    // unit_id: entity?.uuid || '',
    ma_tt: data.ma_tt || '',
    ma_kh: state.khachHang?.uuid || '',
    tk: state.taiKhoan?.uuid || state.khachHang?.account_data?.uuid || '',
    ma_nvbh: state.nhanVien?.uuid || state.khachHang?.sales_rep_data?.uuid || '',
    ma_tthddt: data.ma_tthddt || '',

    // Customer information from state and form
    ma_so_thue: state.khachHang?.tax_code || data.ma_so_thue || '',
    ten_kh_thue: state.khachHang?.customer_name || data.ten_kh_thue || '',
    dia_chi: state.khachHang?.address || data.dia_chi || '',
    ong_ba: data.ong_ba || state.khachHang?.contact_person || '',
    e_mail: data.e_mail || state.khachHang?.email || '',
    dien_giai: state.dienGiai || '',

    // Document dates and references
    ngay_ct: data.ngay_ct || new Date().toISOString().split('T')[0],
    ngay_lct: data.ngay_lct,

    // Currency and exchange rate
    ma_nt: data.ma_nt || '',
    ty_gia: state.ty_gia || 1,
    ...transformDocumentNumber(state.quyenChungTu, state.soChungTuHienTai),
    status: data.status,
    // Status and other fields
    ly_do_huy: data.ly_do_huy || '',
    ly_do: data.ly_do || '',
    ten_vt_thue: data.ten_vt_thue || '',
    ghi_chu: data.ghi_chu || '',

    // E-invoice information
    so_ct_hddt: data.so_ct_hddt || '',
    so_ct2_hddt: data.so_ct2_hddt || '',
    ma_mau_ct_hddt: data.ma_mau_ct_hddt || '',

    // Payment and processing flags
    pt_tao_yn: state.pt_tao_yn,
    transfer_yn: state.transfer_yn,
    ma_httt: state.pt_tao_yn ? state.ma_httt : null,
    ma_pttt: data.ma_pttt || '',

    // Additional references
    ma_ngv: '1',
    ma_kh9: state.coQuanThue?.uuid || '',

    // Financial totals
    t_tien_nt2: roundNumber(calculateTotals.tong_tien),
    t_tien2: roundNumber(calculateTotals.tong_tien),
    t_thue_nt: roundNumber(calculateTotals.thue),
    t_thue: roundNumber(calculateTotals.thue),
    t_ck_nt: roundNumber(calculateTotals.tong_ck),
    t_ck: roundNumber(calculateTotals.tong_ck),
    t_tt_nt: roundNumber(calculateTotals.tong_thanh_toan),
    t_tt: roundNumber(calculateTotals.tong_thanh_toan),

    // Detail arrays
    chi_tiet
  };
};

const roundNumber = (number: number) => {
  return Math.round(number * 100) / 100;
};
